import {
  ProjectCategory,
  PROJECTS_SORT_OPTIONS,
  RELEASE_GROUPS_SORT_OPTIONS,
} from '../../shared/projectsV2';
import { parsePaginationQueryParams } from './pagination';
import type { AnySort } from '../../shared/projectsV2';
import type { HydrateStateQuery } from '../redux/projectsV2/types';

/**
 * Check if a given project category is valid.
 *
 * @param {ProjectCategory} category - A given category to test.
 */
export const isValidCategory = (category: ProjectCategory): category is ProjectCategory =>
  Object.values(ProjectCategory).includes(category);

/**
 * Takes the category and any sort,
 * returns true if a given sort is valid based on that category.
 *
 * @param {ProjectCategory} category - A project category.
 * @param {AnySort} sort - Any valid sort string (ie: 'issues-total_asc')
 */
export function isValidSort<S extends AnySort>(category: ProjectCategory, sort: S): sort is S {
  switch (category) {
    case 'projects':
      return PROJECTS_SORT_OPTIONS.includes(sort);
    case 'releaseGroups':
      return RELEASE_GROUPS_SORT_OPTIONS.includes(sort);
    default:
      return false;
  }
}

/**
 * Handles converting the query params into their primitive values.
 * This is needed because the parsed params will all be strings,
 * for example `count=123` would parse to `'123'` and needs to be converted
 * to a number before adding it to the state/comparing to current state values.
 */
export const parseQueryParams = (query: Partial<HydrateStateQuery>): Partial<HydrateStateQuery> => {
  const parsed: Partial<HydrateStateQuery> = parsePaginationQueryParams<HydrateStateQuery>(query);

  if (query.category) {
    parsed.category = query.category;
  }

  if (query.sort) {
    parsed.sort = query.sort;
  }

  if (query.title) {
    parsed.title = query.title;
  }

  // Filters are of type Record<string, string[]>
  if (query.filter) {
    parsed.filter = query.filter;
  }

  return parsed;
};
